import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Divider, Button, TextField, Avatar, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { ArrowDropDownRounded as ArrowDropDownRoundedIcon } from '@mui/icons-material';
import { getCardById } from 'apis/cardApi';
import { createLead } from 'apis/leadCaptureApi';
import { useParams } from 'react-router-dom';
import { AddIcon } from 'icons/materialIcons';
import { darken } from '@mui/system';
import { validateEmail, validatePhoneNumber } from 'utils/validator';
import { useAuth } from 'contexts/AuthContext';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useEventTracking } from 'services/eventTrackingService';
import TeamPhoneInputField from 'team/shared/components/TeamPhoneInputField';
import { useCardContext } from 'contexts/CardContext';
import { normalizeFields, getFieldOptions } from './fieldNormalization';

const TeamLeadCaptureOverlay = ({
    onClose,
    onSuccess,
    addMoreConnectButtonColor = '#2f82ed',
    cardLayoutType,
    triggerSource = 'auto'
}) => {
    const tracking = useEventTracking();
    const { cardState } = useCardContext();
    const [submitting, setSubmitting] = useState(false);
    const [showMoreFields, setShowMoreFields] = useState(false);
    const { cardId } = useParams();

    // Debug: Log when overlay mounts
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🔍 Overlay: Component mounted/opened', { cardId });
        }
    }, [cardId]);
    const [cardDetails, setCardDetails] = useState({});
    const [cardProfilePictureUrl, setCardProfilePictureUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [cardFullName, setCardFullName] = useState('');
    const [cardEmail, setCardEmail] = useState('');
    const [inputFieldsConfig, setInputFieldsConfig] = useState([
        { label: 'Full Name', required: false, type: 'text' },
        { label: 'Email', required: true, type: 'text' },
        { label: 'Phone Number', required: false, type: 'text' },
        { label: 'Job Title', required: false, type: 'text' },
        { label: 'Company', required: false, type: 'text' },
        { label: 'Note', required: false, type: 'text' }
    ]);
    const { authState } = useAuth();

    // FIXED: Safely get userId, default to card owner's userId for unauthenticated users
    const userId = authState?.user?.userId || cardDetails?.card?.userId || null;
    const isAuthenticated = authState?.isAuthenticated || false;

    // Form data state
    const [leadName, setLeadName] = useState('');
    const [leadEmail, setLeadEmail] = useState('');
    const [leadPhoneNumber, setLeadPhoneNumber] = useState('');
    const [leadJobTitle, setLeadJobTitle] = useState('');
    const [leadCompany, setLeadCompany] = useState('');
    const [leadNotes, setLeadNotes] = useState('');

    // Dynamic form state for additional fields
    const [dynamicFieldValues, setDynamicFieldValues] = useState({});
    const [dynamicFieldErrors, setDynamicFieldErrors] = useState({});

    // State for dropdown field values
    const [dropdownFieldValues, setDropdownFieldValues] = useState({});

    // Error states
    const [leadNameError, setLeadNameError] = useState('');
    const [leadEmailError, setLeadEmailError] = useState('');
    const [leadPhoneNumberError, setLeadPhoneNumberError] = useState('');
    const [formSubmitted, setFormSubmitted] = useState(false);

    // DYNAMIC modal position that adjusts when more fields are shown
    const MODAL_POSITION = triggerSource === 'auto' ? {
        top: {
            xs: showMoreFields ? '5vh' : '18vh',
            sm: showMoreFields ? '8vh' : '22vh',
            md: showMoreFields ? '10vh' : '25vh'
        },
        transform: 'translate(-50%, 0)',
    } : {
        top: {
            xs: showMoreFields ? '15%' : '35%',
            sm: showMoreFields ? '20%' : '40%',
            md: showMoreFields ? '25%' : '45%'
        },
        transform: 'translate(-50%, -50%)',
    };

    const BASE_URL = `${FRONTEND_BASE_URL}/card`;
    const cardUrl = `${BASE_URL}/${cardId}`;

    // Helper function to get field config by default label
    // Uses label matching first, then falls back to position-based lookup for backward compatibility
    const getFieldConfigByDefaultLabel = useCallback((defaultLabel) => {
        // First, try to find by matching the default label names in the config
        // This handles cases where labels have been customized
        const defaultLabelVariations = {
            'Full Name': ['Full Name', 'Name', 'fullName'],
            'Email': ['Email', 'email'],
            'Phone Number': ['Phone Number', 'Phone', 'phoneNumber'],
            'Job Title': ['Job Title', 'Title', 'jobTitle'],
            'Company': ['Company', 'company'],
            'Note': ['Note', 'Notes', 'note', 'Notes On This Interaction']
        };

        const variations = defaultLabelVariations[defaultLabel] || [defaultLabel];

        // Try to find by label match first (handles custom labels)
        for (const variation of variations) {
            const found = inputFieldsConfig.find(f =>
                f.label === variation ||
                f.label.toLowerCase() === variation.toLowerCase()
            );
            if (found) return found;
        }

        // Fallback: try position-based lookup (for backward compatibility with default labels)
        const defaultLabelMap = {
            'Full Name': 0,
            'Email': 1,
            'Phone Number': 2,
            'Job Title': 3,
            'Company': 4,
            'Note': 5
        };
        const index = defaultLabelMap[defaultLabel];
        if (index !== undefined && inputFieldsConfig[index]) {
            // Check if the field at this position matches the expected default label
            const fieldAtPosition = inputFieldsConfig[index];
            const matchesDefault = variations.some(v =>
                fieldAtPosition.label === v ||
                fieldAtPosition.label.toLowerCase() === v.toLowerCase()
            );
            if (matchesDefault) {
                return fieldAtPosition;
            }
        }

        return undefined;
    }, [inputFieldsConfig]);

    // Helper function to check if a field exists in the config
    const fieldExists = useCallback((defaultLabel) => {
        return getFieldConfigByDefaultLabel(defaultLabel) !== undefined;
    }, [getFieldConfigByDefaultLabel]);

    // Helper function to check if a field is required
    const isFieldRequired = useCallback((defaultLabel) => {
        const field = getFieldConfigByDefaultLabel(defaultLabel);
        return field ? field.required : false;
    }, [getFieldConfigByDefaultLabel]);

    // Helper function to get the custom label for a field
    const getFieldLabel = useCallback((defaultLabel) => {
        const field = getFieldConfigByDefaultLabel(defaultLabel);
        return field ? field.label : defaultLabel;
    }, [getFieldConfigByDefaultLabel]);

    // Helper function to check if a field is a standard field (has special handling)
    const isStandardField = useCallback((label) => {
        const standardFields = ['Full Name', 'Email', 'Phone Number', 'Job Title', 'Company', 'Note'];
        return standardFields.some(sf => {
            const field = getFieldConfigByDefaultLabel(sf);
            return field && field.label === label;
        });
    }, [getFieldConfigByDefaultLabel]);

    // Get all fields that should be rendered (excluding standard fields that are already handled)
    const getAdditionalFields = useCallback(() => {
        if (!inputFieldsConfig || inputFieldsConfig.length === 0) return [];

        const standardFieldLabels = new Set();
        ['Full Name', 'Email', 'Phone Number', 'Job Title', 'Company', 'Note'].forEach(defaultLabel => {
            const field = getFieldConfigByDefaultLabel(defaultLabel);
            if (field) {
                standardFieldLabels.add(field.label);
            }
        });

        const additionalFields = inputFieldsConfig.filter(field => {
            // Include text fields and dropdown fields that are not standard fields
            const isTextField = field.type === 'text' || !field.type;
            const isDropdownField = field.type === 'dropdown';
            const isNotStandard = !standardFieldLabels.has(field.label);
            return (isTextField || isDropdownField) && isNotStandard;
        });

        // Debug logging (remove in production)
        if (process.env.NODE_ENV === 'development') {
            console.log('🔍 getAdditionalFields:', {
                totalFields: inputFieldsConfig.length,
                allFields: inputFieldsConfig.map(f => {
                    const options = f.type === 'dropdown' ? getFieldOptions(f) : undefined;
                    return {
                        label: f.label,
                        type: f.type || 'undefined',
                        options: options,
                        config: f.config,
                        isStandard: standardFieldLabels.has(f.label),
                        willBeIncluded: (f.type === 'text' || !f.type || f.type === 'dropdown') && !standardFieldLabels.has(f.label)
                    };
                }),
                standardFieldLabels: Array.from(standardFieldLabels),
                additionalFieldsCount: additionalFields.length,
                additionalFields: additionalFields.map(f => {
                    const options = f.type === 'dropdown' ? getFieldOptions(f) : undefined;
                    return {
                        label: f.label,
                        type: f.type,
                        options: options,
                        config: f.config,
                        hasOptions: f.type === 'dropdown' ? (options && options.length > 0 && options.some(opt => opt && opt.trim())) : undefined
                    };
                })
            });
        }

        return additionalFields;
    }, [inputFieldsConfig, getFieldConfigByDefaultLabel]);

    // Handle dynamic field changes
    const handleDynamicFieldChange = useCallback((fieldLabel, value) => {
        setDynamicFieldValues(prev => ({
            ...prev,
            [fieldLabel]: value
        }));

        if (formSubmitted) {
            const field = inputFieldsConfig.find(f => f.label === fieldLabel);
            if (field && field.required && !value.trim()) {
                setDynamicFieldErrors(prev => ({
                    ...prev,
                    [fieldLabel]: `${fieldLabel} is required`
                }));
            } else {
                setDynamicFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldLabel];
                    return newErrors;
                });
            }
        }
    }, [formSubmitted, inputFieldsConfig]);

    // STABLE event handlers
    const handleAddMoreClick = useCallback(() => {
        setShowMoreFields(true);
    }, []);

    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    }, [onClose]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;

        switch (name) {
            case 'name':
                setLeadName(value);
                if (formSubmitted) {
                    const nameRequired = isFieldRequired('Full Name');
                    const nameLabel = getFieldLabel('Full Name');
                    if (nameRequired && !value.trim()) {
                        setLeadNameError(`${nameLabel} is required`);
                    } else if (value && value.trim().length === 0) {
                        setLeadNameError('Name cannot be empty');
                    } else {
                        setLeadNameError('');
                    }
                }
                break;
            case 'email':
                setLeadEmail(value);
                if (formSubmitted) {
                    const emailRequired = isFieldRequired('Email');
                    const emailLabel = getFieldLabel('Email');
                    if (emailRequired && !value) {
                        setLeadEmailError(`${emailLabel} is required`);
                    } else if (value && !validateEmail(value)) {
                        setLeadEmailError('Invalid email address!');
                    } else {
                        setLeadEmailError('');
                    }
                }
                break;
            case 'jobTitle':
                setLeadJobTitle(value);
                break;
            case 'company':
                setLeadCompany(value);
                break;
            case 'notes':
                setLeadNotes(value);
                break;
            default:
                break;
        }
    }, [formSubmitted, isFieldRequired, getFieldLabel]);

    const handlePhoneChange = useCallback((value) => {
        setLeadPhoneNumber(value);
        if (formSubmitted) {
            const phoneRequired = isFieldRequired('Phone Number');
            const phoneLabel = getFieldLabel('Phone Number');
            if (phoneRequired && !value) {
                setLeadPhoneNumberError(`${phoneLabel} is required`);
            } else if (value && !validatePhoneNumber(value)) {
                setLeadPhoneNumberError('Please enter a valid phone number');
            } else {
                setLeadPhoneNumberError('');
            }
        } else if (value && !validatePhoneNumber(value)) {
            setLeadPhoneNumberError('Please enter a valid phone number');
        } else {
            setLeadPhoneNumberError('');
        }
    }, [formSubmitted, isFieldRequired, getFieldLabel]);

    // STABLE validateForm function - uses inputFieldsConfig to determine required fields
    const validateForm = useCallback(() => {
        let isValid = true;

        // Validate Email - check if required from config
        const emailRequired = isFieldRequired('Email');
        if (emailRequired && !leadEmail) {
            setLeadEmailError('Email is required');
            isValid = false;
        } else if (leadEmail && !validateEmail(leadEmail)) {
            setLeadEmailError('Invalid email address!');
            isValid = false;
        } else {
            setLeadEmailError('');
        }

        // Validate Full Name - check if required from config
        const nameRequired = isFieldRequired('Full Name');
        const nameLabel = getFieldLabel('Full Name');
        if (nameRequired && !leadName.trim()) {
            setLeadNameError(`${nameLabel} is required`);
            isValid = false;
        } else if (leadName && leadName.trim().length === 0) {
            setLeadNameError('Name cannot be empty');
            isValid = false;
        } else {
            setLeadNameError('');
        }

        // Validate Phone Number - check if required from config
        const phoneRequired = isFieldRequired('Phone Number');
        const phoneLabel = getFieldLabel('Phone Number');
        if (phoneRequired && !leadPhoneNumber) {
            setLeadPhoneNumberError(`${phoneLabel} is required`);
            isValid = false;
        } else if (leadPhoneNumber && !validatePhoneNumber(leadPhoneNumber)) {
            setLeadPhoneNumberError('Please enter a valid phone number');
            isValid = false;
        } else {
            setLeadPhoneNumberError('');
        }

        // Validate Job Title - check if required from config
        const jobTitleRequired = isFieldRequired('Job Title');
        if (jobTitleRequired && !leadJobTitle.trim()) {
            // Note: Job Title error would need to be added if you want to show it
            isValid = false;
        }

        // Validate Company - check if required from config
        const companyRequired = isFieldRequired('Company');
        if (companyRequired && !leadCompany.trim()) {
            // Note: Company error would need to be added if you want to show it
            isValid = false;
        }

        // Validate Note - check if required from config
        const noteRequired = isFieldRequired('Note');
        if (noteRequired && !leadNotes.trim()) {
            // Note: Note error would need to be added if you want to show it
            isValid = false;
        }

        // Validate dynamic fields (text and dropdown)
        const additionalFields = getAdditionalFields();
        for (const field of additionalFields) {
            if (field.required) {
                if (field.type === 'dropdown') {
                    const value = dropdownFieldValues[field.label] || '';
                    if (!value) {
                        setDynamicFieldErrors(prev => ({
                            ...prev,
                            [field.label]: `${field.label} is required`
                        }));
                        isValid = false;
                    }
                } else {
                    const value = dynamicFieldValues[field.label] || '';
                    if (!value.trim()) {
                        setDynamicFieldErrors(prev => ({
                            ...prev,
                            [field.label]: `${field.label} is required`
                        }));
                        isValid = false;
                    }
                }
            }
        }

        return isValid;
    }, [leadEmail, leadName, leadPhoneNumber, leadJobTitle, leadCompany, leadNotes, dynamicFieldValues, dropdownFieldValues, isFieldRequired, getFieldLabel, getAdditionalFields]);

    // STABLE handleSubmit - FIXED to work with unauthenticated users
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormSubmitted(true);

        if (!validateForm()) {
            return;
        }

        // Don't proceed if we don't have a userId (neither authenticated user nor card owner)
        if (!userId) {
            console.error('No user ID available for lead capture');
            return;
        }

        setSubmitting(true);

        const cardData = { cardUrl, cardEmail };

        // Build customFields object from dynamic field values (text and dropdown)
        const customFields = {};
        const additionalFields = getAdditionalFields();
        additionalFields.forEach(field => {
            if (field.type === 'dropdown') {
                const value = dropdownFieldValues[field.label] || '';
                if (value) {
                    customFields[field.label] = {
                        value: value,
                        isRequired: field.required || false,
                        isHidden: false
                    };
                }
            } else {
                const value = dynamicFieldValues[field.label] || '';
                if (value.trim()) {
                    customFields[field.label] = {
                        value: value.trim(),
                        isRequired: field.required || false,
                        isHidden: false
                    };
                }
            }
        });

        try {
            const leadResponse = await createLead(
                {
                    leadName,
                    leadEmail,
                    leadPhoneNumber,
                    leadJobTitle,
                    leadCompany,
                    leadNotes,
                    customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
                    cardId: cardId,
                    leadSource: 'lead-capture-form',
                    userId // This will be either authenticated user's ID or card owner's ID
                },
                cardData,
                authState?.token || null // Pass token if available, null if not authenticated
            );

            const leadId = typeof leadResponse === 'string' ? leadResponse : (leadResponse._id || 'unknown');

            if (tracking) {
                tracking.trackLeadCapture({
                    leadId,
                    leadName,
                    leadEmail,
                    leadSource: 'lead-capture-form',
                    cardId: cardId,
                    timestamp: new Date().toISOString(),
                    isAuthenticated
                });
            }

            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving lead:', error);
            if (error.response && error.response.data) {
                const errorMessage = error.response.data.message;
                if (errorMessage.includes('email')) {
                    setLeadEmailError(errorMessage);
                } else if (errorMessage.includes('name')) {
                    setLeadNameError(errorMessage);
                } else if (errorMessage.includes('phone')) {
                    setLeadPhoneNumberError(errorMessage);
                }
            }
        } finally {
            setSubmitting(false);
        }
    }, [
        leadName, leadEmail, leadPhoneNumber, leadJobTitle, leadCompany, leadNotes,
        cardId, userId, validateForm, cardUrl, cardEmail, authState?.token,
        tracking, onSuccess, onClose, isAuthenticated
    ]);

    // Fetch card details - STABLE with single execution
    useEffect(() => {
        let isMounted = true;

        const fetchCardDetails = async () => {
            if (!cardId) return;

            try {
                const data = await getCardById(cardId);
                if (isMounted) {
                    setCardDetails(data);
                    setLoading(false);
                    // Update input fields config immediately when card data is fetched
                    if (data?.card?.leadCaptureInputFields) {
                        // Normalize fields to use config structure
                        const fieldsWithType = normalizeFields(data.card.leadCaptureInputFields);
                        setInputFieldsConfig(fieldsWithType);

                        // Debug logging
                        if (process.env.NODE_ENV === 'development') {
                            console.log('🔍 Overlay: Fetched from server', fieldsWithType);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching card details:', error);
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchCardDetails();

        return () => {
            isMounted = false;
        };
    }, [cardId]);

    // ✅ CRITICAL: Prioritize card context data (real-time updates) over server data
    // This ensures newly added fields appear immediately even before saving
    useEffect(() => {
        let fieldsToUse = null;
        let source = 'none';

        // First check card context (has real-time updates)
        if (cardId && cardState[cardId]?.leadCaptureInputFields) {
            const contextFields = cardState[cardId].leadCaptureInputFields;
            if (contextFields && Array.isArray(contextFields) && contextFields.length > 0) {
                fieldsToUse = normalizeFields(contextFields);
                source = 'context';

                // Debug: Log normalized fields from context
                if (process.env.NODE_ENV === 'development') {
                    console.log('🔍 Overlay: Fields from context:', fieldsToUse.map(f => ({
                        label: f.label,
                        type: f.type,
                        config: f.config,
                        options: getFieldOptions(f)
                    })));
                }
            }
        }

        // Fallback to server data if context doesn't have it
        if (!fieldsToUse && cardDetails?.card?.leadCaptureInputFields) {
            const serverFields = cardDetails.card.leadCaptureInputFields;
            if (serverFields && Array.isArray(serverFields) && serverFields.length > 0) {
                fieldsToUse = normalizeFields(serverFields);
                source = 'server';

                // Debug: Log normalized fields from server
                if (process.env.NODE_ENV === 'development') {
                    console.log('🔍 Overlay: Fields from server:', fieldsToUse.map(f => ({
                        label: f.label,
                        type: f.type,
                        config: f.config,
                        options: getFieldOptions(f)
                    })));
                }
            }
        }

        if (fieldsToUse) {
            setInputFieldsConfig(fieldsToUse);

            // Debug logging
            if (process.env.NODE_ENV === 'development') {
                console.log('🔍 Overlay: Updated fields from', source, {
                    cardId,
                    fieldsCount: fieldsToUse.length,
                    fields: fieldsToUse.map(f => ({
                        label: f.label,
                        type: f.type || 'undefined',
                        required: f.required,
                        config: f.config,
                        options: f.type === 'dropdown' ? getFieldOptions(f) : undefined,
                        fullField: f // Include full field object for debugging
                    }))
                });
            }
        } else {
            // Debug logging when no fields found
            if (process.env.NODE_ENV === 'development') {
                console.log('🔍 Overlay: No fields found', {
                    cardId,
                    hasContext: !!(cardId && cardState[cardId]?.leadCaptureInputFields),
                    hasServer: !!(cardDetails?.card?.leadCaptureInputFields),
                    contextFields: cardId && cardState[cardId]?.leadCaptureInputFields,
                    serverFields: cardDetails?.card?.leadCaptureInputFields
                });
            }
        }
    }, [cardId, cardState, cardDetails]);

    // Set card info - STABLE
    useEffect(() => {
        if (cardDetails?.card && !cardFullName) {
            setCardFullName(cardDetails.card.contact_info?.fullName || '');
            setCardProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
            setCardEmail(cardDetails.card.contact_info?.email || '');
        }
    }, [cardDetails?.card, cardFullName]);

    // Don't render if we don't have essential data
    if (!userId && !loading) {
        console.warn('Lead capture overlay: No user ID available');
        return null;
    }

    return (
        <>
            {/* Backdrop */}
            <Box
                onClick={handleBackdropClick}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9999,
                }}
            />

            {/* Overlay */}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: { xs: '95%', sm: '90%', md: '400px' },
                    maxWidth: '400px',
                    margin: '0 auto',
                    backgroundColor: '#fff',
                    padding: { xs: '16px', sm: '20px', md: '24px' },
                    borderRadius: { xs: '16px', sm: '20px' },
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    position: 'fixed',
                    left: '50%',
                    zIndex: 10000,
                    ...MODAL_POSITION,
                    overflow: 'visible',
                    maxHeight: showMoreFields ? { xs: '98vh', sm: '95vh', md: '90vh' } : { xs: '85vh', sm: '80vh', md: '75vh' },
                    transition: 'top 0.3s ease-in-out', // Smooth transition when position changes
                    '@media (max-width: 600px)': {
                        margin: '0 8px',
                        width: 'calc(100% - 16px)',
                        maxWidth: 'calc(100% - 16px)',
                    }
                }}
            >
                {/* Profile Picture */}
                {cardProfilePictureUrl && (
                    <Avatar
                        src={cardProfilePictureUrl}
                        alt="Profile Picture"
                        sx={{
                            width: { xs: 70, sm: 80, md: 90 },
                            height: { xs: 70, sm: 80, md: 90 },
                            position: 'absolute',
                            top: {
                                xs: '-35px',
                                sm: '-40px',
                                md: '-45px'
                            },
                            left: '50%',
                            transform: 'translateX(-50%)',
                            border: { xs: '3px solid white', sm: '4px solid white' },
                            boxShadow: '0 6px 15px rgba(0, 0, 0, 0.3)',
                            zIndex: 10001,
                        }}
                    />
                )}

                <Box sx={{
                    marginTop: { xs: '40px', sm: '50px', md: '60px' },
                    px: { xs: 0, sm: 1 }
                }}>
                    <Typography
                        variant="body2"
                        sx={{
                            marginBottom: { xs: 1.5, sm: 2 },
                            fontWeight: '700',
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                            textAlign: 'center',
                            lineHeight: 1.3
                        }}
                    >
                        Share your info with {cardFullName}
                    </Typography>

                    <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

                    {/* Email Field - only show if exists in config */}
                    {fieldExists('Email') && (
                        <TextField
                            label={getFieldLabel('Email')}
                            variant="outlined"
                            fullWidth
                            name="email"
                            value={leadEmail}
                            onChange={handleInputChange}
                            error={Boolean(leadEmailError)}
                            helperText={leadEmailError}
                            required={isFieldRequired('Email')}
                            sx={{
                                mb: 1,
                                '& .MuiOutlinedInput-root': {
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                },
                            }}
                        />
                    )}

                    {/* Name Field - only show if exists in config */}
                    {fieldExists('Full Name') && (
                        <TextField
                            label={getFieldLabel('Full Name')}
                            variant="outlined"
                            fullWidth
                            name="name"
                            value={leadName}
                            onChange={handleInputChange}
                            error={Boolean(leadNameError)}
                            helperText={leadNameError}
                            required={isFieldRequired('Full Name')}
                            sx={{
                                mb: 1,
                                '& .MuiOutlinedInput-root': {
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                },
                            }}
                        />
                    )}

                    {/* Phone Number Field - only show if exists in config */}
                    {fieldExists('Phone Number') && (
                        <TeamPhoneInputField
                            value={leadPhoneNumber}
                            onChange={handlePhoneChange}
                            error={leadPhoneNumberError}
                            country="sg"
                            containerSx={{ mb: 1 }}
                            required={isFieldRequired('Phone Number')}
                            label={getFieldLabel('Phone Number')}
                        />
                    )}

                    {/* Add More Button */}
                    {!showMoreFields && (
                        <Button
                            onClick={handleAddMoreClick}
                            sx={{
                                color: addMoreConnectButtonColor,
                                fontWeight: 'bold',
                                backgroundColor: 'transparent',
                                textTransform: 'none',
                                marginBottom: { xs: '12px', sm: '16px' },
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                padding: { xs: '8px 12px', sm: '10px 16px' },
                            }}
                        >
                            <AddIcon sx={{
                                marginRight: '5px',
                                color: addMoreConnectButtonColor,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }} />
                            ADD MORE
                        </Button>
                    )}

                    {/* Additional Fields - only show if exists in config */}
                    {showMoreFields && (
                        <>
                            {fieldExists('Job Title') && (
                                <TextField
                                    label={getFieldLabel('Job Title')}
                                    variant="outlined"
                                    fullWidth
                                    name="jobTitle"
                                    value={leadJobTitle}
                                    onChange={handleInputChange}
                                    required={isFieldRequired('Job Title')}
                                    sx={{ mb: 1, mt: 1 }}
                                />
                            )}

                            {fieldExists('Company') && (
                                <TextField
                                    label={getFieldLabel('Company')}
                                    variant="outlined"
                                    fullWidth
                                    name="company"
                                    value={leadCompany}
                                    onChange={handleInputChange}
                                    required={isFieldRequired('Company')}
                                    sx={{ mb: 1 }}
                                />
                            )}

                            {fieldExists('Note') && (
                                <TextField
                                    label={getFieldLabel('Note')}
                                    variant="outlined"
                                    fullWidth
                                    name="notes"
                                    value={leadNotes}
                                    onChange={handleInputChange}
                                    required={isFieldRequired('Note')}
                                    multiline
                                    rows={3}
                                    sx={{ mb: 1 }}
                                />
                            )}

                            {/* Render additional dynamic fields (text and dropdown) */}
                            {getAdditionalFields().length > 0 && (
                                <>
                                    {getAdditionalFields().map((field, index) => {
                                        const isDropdown = field.type === 'dropdown';
                                        // Check if dropdown has non-empty options
                                        const options = getFieldOptions(field);
                                        const hasOptions = options.length > 0 && options.some(opt => opt && opt.trim());

                                        // Debug logging for dropdown fields
                                        if (isDropdown && process.env.NODE_ENV === 'development') {
                                            console.log('🔍 Dropdown field found:', {
                                                label: field.label,
                                                type: field.type,
                                                options: options,
                                                hasOptions: hasOptions,
                                                config: field.config
                                            });
                                        }

                                        if (isDropdown && hasOptions) {
                                            return (
                                                <FormControl
                                                    key={`dynamic-${index}-${field.label}`}
                                                    fullWidth
                                                    required={field.required}
                                                    sx={{ mb: 1 }}
                                                >
                                                    <Select
                                                        value={dropdownFieldValues[field.label] || ''}
                                                        onChange={(e) => setDropdownFieldValues(prev => ({
                                                            ...prev,
                                                            [field.label]: e.target.value
                                                        }))}
                                                        displayEmpty
                                                        IconComponent={ArrowDropDownRoundedIcon}
                                                        sx={{
                                                            '& .MuiSelect-icon': {
                                                                right: '8px',
                                                                color: '#828282',
                                                            },
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#c5c5c5',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#828282',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#2f82ed',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="" disabled>
                                                            <em>{field.label}</em>
                                                        </MenuItem>
                                                        {options.filter(opt => opt && opt.trim()).map((option, optIndex) => (
                                                            <MenuItem key={optIndex} value={option}>
                                                                {option}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            );
                                        }

                                        return (
                                            <TextField
                                                key={`dynamic-${index}-${field.label}`}
                                                label={field.label}
                                                variant="outlined"
                                                fullWidth
                                                value={dynamicFieldValues[field.label] || ''}
                                                onChange={(e) => handleDynamicFieldChange(field.label, e.target.value)}
                                                error={Boolean(dynamicFieldErrors[field.label])}
                                                helperText={dynamicFieldErrors[field.label]}
                                                required={field.required}
                                                multiline={field.label.toLowerCase().includes('note') || field.label.toLowerCase().includes('message')}
                                                rows={field.label.toLowerCase().includes('note') || field.label.toLowerCase().includes('message') ? 3 : 1}
                                                sx={{ mb: 1 }}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </>
                    )}


                    {/* Submit Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={submitting || !userId}
                        sx={{
                            marginTop: { xs: 1.5, sm: 2 },
                            textTransform: 'none',
                            borderRadius: '20px',
                            padding: { xs: '12px 20px', sm: '14px 20px', md: '16px 20px' },
                            fontWeight: 'bold',
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                            marginBottom: '4px',
                            backgroundColor: addMoreConnectButtonColor,
                            '&:hover': {
                                backgroundColor: darken(addMoreConnectButtonColor, 0.2),
                            },
                            '&:disabled': {
                                backgroundColor: darken(addMoreConnectButtonColor, 0.3),
                            }
                        }}
                    >
                        {submitting ? 'Connecting...' : 'Connect'}
                    </Button>

                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                            marginTop: { xs: 1.5, sm: 2 },
                            fontWeight: 'bold',
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                            color: '#666666',
                            lineHeight: 1.3,
                            px: { xs: 1, sm: 0 }
                        }}
                    >
                        Touch does not sell or share your data
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

export default TeamLeadCaptureOverlay;